<script lang="ts">
  /**
   * Contacts screen (spec §13): bar with back + import, add form, note line, empty state (strings
   * local to this screen, not in the dictionary) or the list with compose-to and delete per row.
   */
  import { untrack } from 'svelte';
  import { lang, t } from '$lib/i18n';
  import { initialOf } from '$lib/mail/format';
  import { store } from '$lib/store';
  import { tooltip } from '$lib/util/dom';
  import Icon from '../ui/Icon.svelte';
  import { contactsEmptyStrings } from '../settings/_local';

  let name = $state('');
  let email = $state('');
  let busy = $state(false);
  let note = $state('');

  const empty = $derived(contactsEmptyStrings(lang.current));
  const canAdd = $derived(!busy && email.trim() !== '');

  $effect(() => {
    untrack(() => {
      void store.loadContacts();
    });
  });

  async function submit(ev: Event): Promise<void> {
    ev.preventDefault();
    const addr = email.trim();
    if (busy || !addr) return;
    busy = true;
    note = '';
    const ok = await store.addContact(name.trim(), addr);
    busy = false;
    if (ok) {
      name = '';
      email = '';
    } else {
      note = t('netError');
    }
  }

  async function doImport(): Promise<void> {
    if (busy) return;
    busy = true;
    note = '';
    const n = await store.importContacts();
    busy = false;
    note = n === null ? t('netError') : t('importedN', { n });
  }

  async function del(id: string): Promise<void> {
    if (!(await store.deleteContact(id))) note = t('netError');
  }
</script>

<div class="contacts">
  <header class="bar">
    <button type="button" class="ghost" aria-label={t('back')} onclick={() => store.backToList()} use:tooltip>
      <span class="flip-ltr"><Icon name="back" size={20} /></span>
    </button>
    <h2>{t('contacts')}</h2>
    <span class="grow"></span>
    <button type="button" class="ghost import-bar" aria-label={t('importRc')} disabled={busy} onclick={() => void doImport()} use:tooltip>
      <Icon name="refresh" size={18} />
    </button>
  </header>

  <div class="body">
    <form class="card add" onsubmit={(ev) => void submit(ev)}>
      <input type="text" dir="auto" autocomplete="off" placeholder={t('contactName')} aria-label={t('contactName')} bind:value={name} />
      <input type="email" dir="ltr" autocomplete="off" spellcheck="false" required placeholder="email@example.com" aria-label={t('email')} bind:value={email} />
      <button type="submit" class="primary" disabled={!canAdd}>{t('addContact')}</button>
    </form>

    {#if note}
      <p class="note" role="status" aria-live="polite">{note}</p>
    {/if}

    {#if !store.contacts.length}
      <div class="empty empty-state">
        <h3>{empty.title}</h3>
        <p>{empty.hint}</p>
        <button type="button" class="empty-act" disabled={busy} onclick={() => void doImport()}>
          <Icon name="inbox" size={16} />
          {t('importRc')}
        </button>
      </div>
    {:else}
      <button type="button" class="import" disabled={busy} onclick={() => void doImport()}>
        <Icon name="inbox" size={16} />
        {t('importRc')}
      </button>
      <div class="card list">
        {#each store.contacts as c (c.id)}
          <div class="row" data-id={c.id}>
            <span class="avatar" aria-hidden="true">{initialOf(c.name, c.email)}</span>
            <span class="who">
              <strong dir="auto">{c.name || c.email}</strong>
              {#if c.name}<small dir="ltr">{c.email}</small>{/if}
            </span>
            <button type="button" class="ghost compose-to" aria-label={t('compose')} onclick={() => store.openCompose({ to: c.email })} use:tooltip>
              <Icon name="pen" size={16} />
            </button>
            <button type="button" class="ghost danger" aria-label={t('del')} onclick={() => void del(c.id)} use:tooltip>
              <Icon name="trash" size={16} />
            </button>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  /* ===== component: svelte-1w9k76x (Contacts) — copied verbatim from legacy-components.css ===== */
  .contacts {
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr;
    min-height: 0;
    background: var(--surface-subtle)
  }

  .bar {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    height: 56px;
    padding-inline: var(--space-3);
    background: var(--surface-card);
    border-bottom: 1px solid var(--border-subtle)
  }

  .bar h2 {
    font-size: var(--text-lg);
    color: var(--text-strong)
  }

  .grow {
    flex: 1
  }

  .ghost {
    padding: var(--space-2);
    border-radius: var(--radius-control);
    color: var(--text-muted)
  }

  .ghost:hover {
    background: var(--surface-sunken)
  }

  .ghost:active {
    background: var(--mail-pressed)
  }

  .ghost.danger:hover {
    color: var(--danger-500)
  }

  .ghost:disabled {
    opacity: .42;
    cursor: default
  }

  .ghost:disabled:hover {
    background: none
  }

  .body {
    overflow-y: auto;
    min-height: 0;
    padding: var(--space-4);
    padding-bottom: calc(var(--space-16) + env(safe-area-inset-bottom));
    display: grid;
    gap: var(--space-6);
    align-content: start;
    max-width: 560px;
    width: 100%;
    margin-inline: auto
  }

  .card {
    background: var(--surface-card);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-card);
    overflow: hidden
  }

  .add {
    padding: var(--space-4);
    display: grid;
    gap: var(--space-3)
  }

  .add input {
    block-size: var(--control-lg);
    padding: 0 var(--space-4);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    background: var(--surface-card);
    color: var(--text-body)
  }

  .add input:focus {
    border-color: var(--blue-500);
    box-shadow: var(--ring-focus);
    outline: none
  }

  .primary {
    block-size: var(--control-lg);
    background: var(--blue-500);
    color: var(--text-on-accent);
    border-radius: var(--radius-control);
    font-weight: var(--weight-medium)
  }

  .primary:hover {
    background: var(--blue-600)
  }

  .primary:active {
    transform: scale(.99)
  }

  .primary:disabled {
    opacity: .42
  }

  .import {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    justify-self: start;
    min-block-size: var(--control-md);
    padding: var(--space-2) var(--space-4);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    color: var(--text-body);
    font-size: var(--text-sm)
  }

  .import:hover {
    background: var(--surface-card)
  }

  .import:active {
    background: var(--mail-pressed)
  }

  .import:disabled {
    opacity: .42;
    cursor: default
  }

  .import:disabled:hover {
    background: none
  }

  .note {
    color: var(--text-link);
    font-size: var(--text-sm)
  }

  .empty {
    padding: var(--space-10) var(--space-4);
    text-align: center;
    color: var(--text-faint)
  }

  .empty-state {
    display: grid;
    gap: var(--space-3);
    justify-items: center;
    max-width: 380px;
    margin-inline: auto
  }

  .empty-state h3 {
    font-size: var(--text-lg);
    color: var(--text-strong)
  }

  .empty-state p {
    font-size: var(--text-sm);
    color: var(--text-muted);
    line-height: var(--leading-body)
  }

  .empty-act {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    margin-top: var(--space-2);
    min-block-size: var(--control-lg);
    padding-inline: var(--space-5);
    background: var(--blue-500);
    color: var(--text-on-accent);
    border-radius: var(--radius-control);
    font-weight: var(--weight-medium)
  }

  .empty-act:hover {
    background: var(--blue-600)
  }

  .empty-act:active {
    transform: scale(.99)
  }

  .empty-act:disabled {
    opacity: .42;
    cursor: default
  }

  .row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4)
  }

  .row+.row {
    border-top: 1px solid var(--border-subtle)
  }

  .avatar {
    width: 40px;
    height: 40px;
    border-radius: var(--radius-pill);
    background: var(--blue-100);
    color: var(--blue-700);
    display: grid;
    place-items: center;
    font-weight: var(--weight-semibold);
    flex: none
  }

  /* `html` is outside the component, so the ancestor must be :global or Svelte prunes the rule. */
  :global(html.tik-dark) .avatar {
    background: var(--blue-900);
    color: var(--blue-200)
  }

  .who {
    flex: 1;
    min-width: 0;
    display: grid
  }

  .who strong {
    color: var(--text-strong);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap
  }

  .who small {
    color: var(--text-faint);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap
  }

  /* ===== appended global fixes (2026-09-04) that target Contacts ===== */
  @media (pointer:fine) {
    .body {
      scrollbar-gutter: stable
    }
  }

  .empty-state h3 {
    text-wrap: balance
  }

  .bar h2 {
    min-width: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    overflow-wrap: anywhere
  }

  /* wide panes: a wider single column so the rows breathe */
  @media (min-width:1100px) {
    .body {
      max-width: 900px
    }

    .body>* {
      min-width: 0
    }
  }
</style>
